/**
 * Session lifetime policy — ONE definition, shared by the server that enforces
 * it and the client that anticipates it.
 *
 * The numbers follow NIST SP 800-63B AAL2 reauthentication guidance, which is
 * what "industry standard" means for an admin console holding buyer PII:
 *
 *   - 60 minutes of inactivity  → sign out and re-authenticate
 *   - 12 hours since sign-in    → sign out regardless of activity
 *
 * WHY THE IDLE CLOCK IS NOT "TIME SINCE THE LAST REQUEST".
 *
 * That is the textbook implementation and it does not work in this app. The
 * console polls on its own: inventory every few seconds, the notification
 * badge every 5 minutes, service notifications every 10, the push heartbeat
 * every 3. Under a last-request rule a tab left open on a sales-office monitor
 * renews its own session for ever and NOBODY is ever signed out — which is
 * exactly the reported symptom ("staying logged in after hours"), on top of a
 * cookie that was 12 hours long and rolling.
 *
 * So renewal is an ALLOW-LIST, the same shape this codebase uses for request
 * bodies: exactly one endpoint (`POST /api/auth/activity`) refreshes the idle
 * clock, and the client calls it only when it has seen real user input —
 * pointer, key, wheel, touch, scroll. Every other request, background poll
 * included, is checked against the clock but cannot move it. A query added
 * later with a `refetchInterval` therefore cannot silently defeat the timeout.
 */

/** Sign out after this long with no real user interaction. */
export const IDLE_TIMEOUT_MS = 60 * 60 * 1000;

/** Sign out this long after sign-in even if the user never stops working. */
export const ABSOLUTE_TIMEOUT_MS = 12 * 60 * 60 * 1000;

/**
 * Smallest gap between two activity pings. The client reports how long it has
 * been idle alongside each ping, so this only controls request volume — it
 * does not blunt the 60-minute figure (see `idleForSeconds` on the route).
 */
export const ACTIVITY_PING_INTERVAL_MS = 3 * 60 * 1000;

/** How often the client re-checks its own idle clock. */
export const IDLE_CHECK_INTERVAL_MS = 30 * 1000;

/** 401 `code` the server returns when the idle window has elapsed. */
export const SESSION_IDLE_CODE = 'SESSION_IDLE_TIMEOUT';

/** 401 `code` the server returns when the absolute window has elapsed. */
export const SESSION_ABSOLUTE_CODE = 'SESSION_EXPIRED';

/** The idle window in whole minutes, for user-facing copy. */
export const IDLE_TIMEOUT_MINUTES = Math.round(IDLE_TIMEOUT_MS / 60_000);
